import {
  Calendar,
  Clock,
  Cpu,
  Fingerprint,
  GitBranch,
  Hourglass,
  Layers,
  Tag,
  Terminal,
} from 'lucide-react';
import { CollapsibleSection } from '@/components/shared/CollapsibleSection';
import { useGitHubWebBaseUrl } from '@/hooks/config/useConfig';
import { CommandBlock } from '@reflex/ui/components/content/CommandBlock';
import { DetailField, DetailList } from '@reflex/ui/components/shared/DetailList';
import { formatDateTime, formatElapsedMs } from '@reflex/ui/lib/format';
import { isBaseBlueprint } from '@runloop/reflex-contract';
import type { Blueprint } from '@/lib/types';

const SIZE_LABELS: Record<string, string> = {
  SMALL: 'Small',
  MEDIUM: 'Medium',
  LARGE: 'Large',
  X_LARGE: 'X-Large',
  XX_LARGE: 'XX-Large',
};

const HIDDEN_METADATA_KEYS = new Set(['repo', 'organizationId', 'orgSlug', 'source', 'type']);

interface BlueprintDetailsSectionProps {
  blueprint: Blueprint;
  blueprints: Blueprint[];
  onSelectBlueprint?: (blueprint: Blueprint) => void;
}

export function BlueprintDetailsSection({
  blueprint,
  blueprints,
  onSelectBlueprint,
}: BlueprintDetailsSectionProps) {
  const isBase = isBaseBlueprint(blueprint);
  const githubWebBaseUrl = useGitHubWebBaseUrl();

  const baseBlueprint = blueprint.baseBlueprintName
    ? blueprints.find((bp) => bp.name === blueprint.baseBlueprintName)
    : null;

  const extraMetadata = blueprint.metadata
    ? Object.entries(blueprint.metadata).filter(([key]) => !HIDDEN_METADATA_KEYS.has(key))
    : [];

  return (
    <CollapsibleSection title="Details">
      <DetailList labelWidth="w-32" striped className="w-fit max-w-full">
        <DetailField icon={Fingerprint} label="Blueprint ID" value={blueprint.id} mono copyable />
        <DetailField
          icon={Calendar}
          label="Created"
          value={formatDateTime(blueprint.createTimeMs)}
        />
        {blueprint.buildFinishTimeMs && (
          <>
            <DetailField
              icon={Clock}
              label="Build finished"
              value={formatDateTime(blueprint.buildFinishTimeMs)}
            />
            <DetailField
              icon={Hourglass}
              label="Build duration"
              value={formatElapsedMs(blueprint.buildFinishTimeMs - blueprint.createTimeMs)}
            />
          </>
        )}
        {blueprint.metadata?.repo && (
          <DetailField
            icon={GitBranch}
            label="Repository"
            value={blueprint.metadata.repo}
            href={`${githubWebBaseUrl}/${blueprint.metadata.repo}`}
          />
        )}
        {blueprint.installCommand && (
          <DetailField icon={Terminal} label="Install">
            <CommandBlock commands={blueprint.installCommand} inline compact prompt={false} />
          </DetailField>
        )}
        {blueprint.resourceSize && (
          <DetailField
            icon={Cpu}
            label="Sandbox size"
            value={SIZE_LABELS[blueprint.resourceSize] ?? blueprint.resourceSize}
          />
        )}
        {!isBase && blueprint.baseBlueprintName && (
          <DetailField icon={Layers} label="Base">
            {baseBlueprint && onSelectBlueprint ? (
              <button
                onClick={() => onSelectBlueprint(baseBlueprint)}
                className="text-status-info hover:underline"
              >
                {blueprint.baseBlueprintName}
              </button>
            ) : (
              <span className="text-sm text-foreground/90">{blueprint.baseBlueprintName}</span>
            )}
          </DetailField>
        )}
        {extraMetadata.map(([key, value]) => (
          <DetailField
            key={key}
            icon={Tag}
            label={<span className="capitalize">{key}</span>}
            value={value}
          />
        ))}
      </DetailList>
    </CollapsibleSection>
  );
}
